'use client';
import React, { useEffect, useRef, useState } from 'react';

const floatingCards = [
  {
    id: 1,
    label: 'AI Creator Ad',
    tag: 'TikTok · 9:16',
    color: 'from-violet-900/80 to-purple-950/80',
    accent: 'bg-violet-500',
    emoji: '🎬',
    style: { top: '8%', left: '-2%', rotate: '-4deg' },
    delay: '0s',
  },
  {
    id: 2,
    label: 'Luxury Campaign',
    tag: 'Instagram · 1:1',
    color: 'from-rose-900/80 to-pink-950/80',
    accent: 'bg-rose-500',
    emoji: '✨',
    style: { top: '5%', right: '-2%', rotate: '3deg' },
    delay: '0.3s',
  },
  {
    id: 3,
    label: 'Influencer Reel',
    tag: 'Reels · 9:16',
    color: 'from-amber-900/80 to-orange-950/80',
    accent: 'bg-amber-500',
    emoji: '📱',
    style: { bottom: '15%', left: '0%', rotate: '2deg' },
    delay: '0.6s',
  },
  {
    id: 4,
    label: 'Product Cinematic',
    tag: 'YouTube · 16:9',
    color: 'from-cyan-900/80 to-teal-950/80',
    accent: 'bg-cyan-500',
    emoji: '🎥',
    style: { bottom: '12%', right: '0%', rotate: '-3deg' },
    delay: '0.9s',
  },
];

export default function UGCHero() {
  const [visible, setVisible] = useState(false);
  const orb1Ref = useRef<HTMLDivElement>(null);
  const orb2Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handleMouse = (e: MouseEvent) => {
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      if (orb1Ref.current) {
        orb1Ref.current.style.transform = `translate(${x * 30 - 15}px, ${y * 30 - 15}px)`;
      }
      if (orb2Ref.current) {
        orb2Ref.current.style.transform = `translate(${-x * 20 + 10}px, ${-y * 20 + 10}px)`;
      }
    };
    window.addEventListener('mousemove', handleMouse, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouse);
  }, []);

  return (
    <section className="relative min-h-screen bg-[#080810] flex flex-col items-center justify-center overflow-hidden pt-20">
      {/* Ambient orbs */}
      <div
        ref={orb1Ref}
        className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full pointer-events-none transition-transform duration-700 ease-out"
        style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)', filter: 'blur(40px)' }}
      />
      <div
        ref={orb2Ref}
        className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full pointer-events-none transition-transform duration-700 ease-out"
        style={{ background: 'radial-gradient(circle, rgba(236,72,153,0.1) 0%, transparent 70%)', filter: 'blur(40px)' }}
      />

      {/* Grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* Floating cards — desktop only */}
      <div className="absolute inset-0 pointer-events-none hidden lg:block">
        {floatingCards.map((card) => (
          <div
            key={card.id}
            className={`absolute w-44 transition-all duration-1000 ${visible ? 'opacity-100' : 'opacity-0'}`}
            style={{
              ...card.style,
              transitionDelay: card.delay,
              animation: `ugcFloat${card.id} ${4 + card.id * 0.5}s ease-in-out infinite`,
            }}
          >
            <div
              className={`rounded-2xl border border-white/10 bg-gradient-to-br ${card.color} backdrop-blur-sm p-4 shadow-2xl`}
              style={{ transform: `rotate(${card.style.rotate})` }}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-2 h-2 rounded-full ${card.accent}`} />
                <span className="font-sans text-[10px] text-white/40 tracking-widest uppercase">{card.tag}</span>
              </div>
              <div className="text-2xl mb-2">{card.emoji}</div>
              <div className="font-sans text-xs font-medium text-white/80">{card.label}</div>
              <div className="mt-3 h-1 rounded-full bg-white/10 overflow-hidden">
                <div className={`h-full rounded-full ${card.accent} w-3/4`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        {/* Badge */}
        <div
          className={`mb-8 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-violet-500/30 bg-violet-500/10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
          <span className="font-sans text-xs text-violet-300 tracking-wide">AI-Powered Creator System · Now Available</span>
        </div>

        {/* Headline */}
        <h1
          className={`font-sans font-bold text-4xl md:text-6xl lg:text-7xl text-white leading-[1.08] tracking-tight mb-6 transition-all duration-1000 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          Create Ultra-Realistic{' '}
          <span className="relative inline-block">
            <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              AI UGC Ads
            </span>
          </span>{' '}
          That Actually Feel Human.
        </h1>

        {/* Subheadline */}
        <p
          className={`font-sans text-lg md:text-xl text-white/50 max-w-2xl mx-auto leading-relaxed mb-10 transition-all duration-1000 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          AI UGC Studio™ gives brands, creators, and agencies a complete system to generate influencer-style ads, cinematic product campaigns, and social-first visuals — without a single shoot day.
        </p>

        {/* CTA */}
        <div
          className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-1000 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <a
            href="https://payhip.com/b/H2qt9"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative inline-flex items-center gap-3 px-8 py-4 rounded-full font-sans font-semibold text-white text-base overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7, #ec4899)' }}
          >
            <span className="relative z-10">Get Instant Access</span>
            <svg className="relative z-10 group-hover:translate-x-1 transition-transform duration-200" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8H13M9 4L13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </a>
          <a
            href="#showcase"
            className="font-sans text-sm text-white/40 hover:text-white/70 transition-colors duration-200 flex items-center gap-2"
          >
            See examples
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 2L7 12M2 7L7 12L12 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-30">
        <span className="font-sans text-[10px] tracking-widest uppercase text-white/50">Scroll</span>
        <div className="w-px h-10 bg-white/20 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1/2 bg-violet-400" style={{ animation: 'scrollLine 1.6s ease-in-out infinite' }} />
        </div>
      </div>

      <style jsx>{`
        @keyframes ugcFloat1 { 0%, 100% { transform: translateY(0px) rotate(-4deg); } 50% { transform: translateY(-12px) rotate(-4deg); } }
        @keyframes ugcFloat2 { 0%, 100% { transform: translateY(0px) rotate(3deg); } 50% { transform: translateY(-16px) rotate(3deg); } }
        @keyframes ugcFloat3 { 0%, 100% { transform: translateY(0px) rotate(2deg); } 50% { transform: translateY(-10px) rotate(2deg); } }
        @keyframes ugcFloat4 { 0%, 100% { transform: translateY(0px) rotate(-3deg); } 50% { transform: translateY(-14px) rotate(-3deg); } }
        @keyframes scrollLine { 0% { transform: translateY(-100%); } 100% { transform: translateY(300%); } }
      `}</style>
    </section>
  );
}
